import _ from 'lodash'
import DynamicFieldsHandler from './dynamicFieldsHandler'
import ObjectModel from '../../objectModel'
import ObjectModelPropertyOptions from '../options/objectModelPropertyOptions'
import { IDynamicDataConfig, IDynamicTreeContext } from '~/interfaces/dynamicFieldsInterfaces'
import { DynamicReferenceType } from '~/enums/dynamicReferenceType'
import ObjectModelRelation from '~/models/objectModelRelations/objectModelRelation'
import { ReferencePathNode } from '~/models/referencePaths/referencePathNode'
import Guid from '~/helpers/guid'
import {
  DeterministicDynamicComponentType,
  IDeterministicDynamicFieldsHandler,
  IObjectModelAccountRelationsHandlerConfig,
  TModes
} from '~/interfaces/dynamicFieldsElementOptionsInterfaces'
import { ObjectModelRelationType } from '~/enums/objectModelRelationType'
import { Logger } from '~/services/logger'
import { ReferencePath } from '~/models/referencePaths/referencePath'

type ItemTypes = ObjectModel | ObjectModelRelation

export default class ObjectModelAccountRelationDynamicFieldsHandler extends DynamicFieldsHandler implements IDeterministicDynamicFieldsHandler<ItemTypes>, IObjectModelAccountRelationsHandlerConfig {
  // determining Vue component through the Handler
  COMPONENTS: Array<DeterministicDynamicComponentType> = ['object-model-account-relations'];
  dynamicMessage: string;
  showDynamicMessage: boolean;
  mode: TModes;
  objectModel: ObjectModel;
  allowObjectModelSelection: boolean = true;
  header: string = 'Object Model Properties';
  rootHeader: string;
  elementOptions = new ObjectModelPropertyOptions();
  restrictToTopLevelCollections: boolean | null = null
  restrictToRootModel: boolean | null = null
  traverseRelatedObjectModels: boolean = true
  hideProperties: boolean = true
  showAccountRelations: boolean = true
  previouslySelected: ReferencePath[]
  allowAccountRelationSelection: boolean = true

  constructor(options) {
    super(options);
    this.objectModel = options.objectModel;
    this.previouslySelected = options.previouslySelected
    if (options.header) this.header = options.header;
    if (options.rootHeader) this.rootHeader = options.rootHeader;
  }

  objectModelAccountRelations(objectModel: ObjectModel): ObjectModelRelation[] {
    return objectModel.accountRelations()
  }

  selectAccountRelation(relation: ObjectModelRelation, path: ReferencePathNode[]): IDynamicDataConfig {
    const config: IDynamicDataConfig = {
      type: DynamicReferenceType.ObjectModelRelation,
      id: Guid.create(),
      label: _.escape(relation.label_title),
      path: path,
    };
    return super.select(relation, config);
  }

  objectModelRelations(q: string, objectModel: ObjectModel): ObjectModelRelation[] {
    return objectModel.modelRelations().filter(relation => {
      if (relation.isAccountRelationship) return false;
      return relation.friendlyDescription.match(q)
    });
  }

  inverseObjectModelRelations(q: string, objectModel: ObjectModel): ObjectModelRelation[] {
    return objectModel.inverse_object_model_relations.all.filter(relation => {
      if (relation.isAccountRelationship) return false;
      return relation.friendlyDescription.match(q)
    });
  }

  hasObjectModelRelationshipMatch(q: string, objectModel: ObjectModel): boolean {
    return this.objectModelRelations(q, objectModel).length > 0;
  }

  hasRelatedObjectModelMatch(q: string, relation: ObjectModelRelation): boolean {
    return relation.related_object_models.filter(om => om.title.toLowerCase().match(q)).length > 0
  }

  hasObjectModelDataMatch(q: string, objectModel: ObjectModel): boolean {
    return false
  }

  isDisabled(item: ItemTypes, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): boolean {
    if (this.isPreviouslySelected(item, context)) {
      return true
    }

    if (item instanceof ObjectModelRelation) {
      if (item.relation_type === ObjectModelRelationType.Account) return false

      if (context.inverse) return true

      return this.isSecondOneToMany(item, context)
    }

    return false
  }

  // this has the flaw of redoing the same calculations as isDisabled() again.
  tooltipMessage(item: object, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): string {
    if (this.isPreviouslySelected(item, context)) {
      return 'Already selected'
    }

    if (item instanceof ObjectModelRelation) {
      if (item.relation_type === ObjectModelRelationType.Account) return null

      if (context.inverse) {
        return "Can't traverse an inverse relation"
      }

      if (this.isSecondOneToMany(item, context)) {
        return "Can't traverse from a many that leads to another many"
      }
    }

    return null
  }

  private isSecondOneToMany (relation: ObjectModelRelation, context: Partial<IDynamicTreeContext<ItemTypes>>): boolean {
    return context.isTopLevelCollectionAssigned && this.itemIsCollection(relation)
  }

  private isPreviouslySelected(item, context): boolean {
    let itemPathReferenceIds = context.referencePathNodesToLevel.map((node) => node.reference_id)
    itemPathReferenceIds.push(item.id)

    // some is equivalent to Ruby's .any?
    return this.previouslySelected.some((referencePath) => {
      const previouslySelectedReferenceIds = referencePath.nodes.map((node) => node.reference_id)

      return _.isEqual(previouslySelectedReferenceIds, itemPathReferenceIds)
    })
  }

  // these are copied over from ProcessFlowObjectModelDynamicFieldsHandler
  protected itemIsCollection(item: ItemTypes, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): boolean {
    if (item instanceof ObjectModel) {
      return this.objectModelIsCollection(item, context);
    } else if (item instanceof ObjectModelRelation) {
      return item.relatesToManyFrom(context.inverse)
    }
  }

  protected objectModelIsCollection(item: ObjectModel, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): boolean {
    if (context.parent instanceof ObjectModelRelation) {
      return context.isTopLevelCollectionAssigned || context.parent.relatesToMany;
    } else if (this.objectModel.id === item.id) {
      return false;
    }

    Logger.errorOnce('ObjectModelAccountRelationDynamicFieldsHandler#objectModelIsCollection', 'Expected either argument {parent: ObjectModelRelation} to exist or for argument item: Object Model to match variable reference id.');
  }

  isTopLevelCollection(item: ItemTypes, context: Partial<IDynamicTreeContext<ItemTypes>> = {}): boolean {
    // If top level is already assigned, exit early
    if (context.isTopLevelCollectionAssigned) return false

    if (item instanceof ObjectModel) {
      if (this.itemIsCollection(context.parent, context)) return true
    } else if (item instanceof ObjectModelRelation) {
      return false
    } else {
      return false
    }
    return false
  }
}